import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fmFileUpload } from "../config";
import useIsDesktop from "../../../utilities/customHooks/useIsDesktop";
import { validateFilesWithErrors } from "../FMListing/utility/fileUtility";
import { FileWithMetaType, FMUploadFIlesHookInterface, SelectedFileForPreviewInterface } from "../types";
import { showCustomToast } from "../../../globalComponents/customToast/toastUtils";
import { trackEvent } from '../../../modules/analytics/trackingMiddleware';
import { FILE_UPLOADED } from '../../../modules/analytics/events';

const useFMUploadFiles = ({ queryKeyData, handleClose }: FMUploadFIlesHookInterface) => {
  const [selectedFilesCollection, setSelectedFilesCollection] = useState<FileWithMetaType[] | []>([]);
  const { assetId = '' } = useParams() as { assetId: string };
  const addMoreInputRef = useRef<HTMLInputElement>(null);
  const isTableAndAbove = useIsDesktop(767);
  const [selectedFileForPreview, setSelectedFileForPreview] = useState<SelectedFileForPreviewInterface | null>(null);
  const queryClient: any = useQueryClient();
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictFiles, setConflictFiles] = useState<number[]>([]);

  const handleAddMoreClick = () => {
    if (addMoreInputRef.current) {
      addMoreInputRef.current.value = "";
      addMoreInputRef.current.click();
    }
  };

  const { mutate: handleUpload, isPending: isUploading } = useMutation({
    mutationFn: fmFileUpload,
    onSuccess: (data: any) => {
      if (data?.existingFiles?.length > 0) {
        setShowConflictDialog(true);
        const selectedFile: FileWithMetaType[] = [];
        data?.existingFiles.forEach((file: any) => {
          selectedFile.push(selectedFilesCollection[file.idx]);
        });
        setSelectedFilesCollection(selectedFile);
        toast.error(`File(s) already exists, please select a different file or choose to replace it.`);
        return;
      }
      setSelectedFilesCollection([]);
      setSelectedFileForPreview(null);
      queryClient.invalidateQueries({ queryKey: queryKeyData });
      toast.success(data?.message || `File Uploaded Successfully`);
      trackEvent(FILE_UPLOADED, { fileCount: selectedFilesCollection.length, assetId: assetId || '', fileNames: selectedFilesCollection.map((f: FileWithMetaType) => f?.file?.name || '').join(', ') });
      handleClose();
    },
    onError: (error: any) => {
      toast.error(error?.message || `Problem in uploading File`);
      console.error('Failed to delete Formula:', error);
    },
  });

  const handleSave = (mode: string = '') => {
    const selectedFiles = selectedFilesCollection as FileWithMetaType[];
    const formData = new FormData();
    formData.append("assetId", assetId);
    selectedFiles.forEach((fileObj, index) => {
      formData.append(`files[${index}]`, fileObj?.file);
      formData.append(`category[${index}]`, fileObj?.category ?? '');
      formData.append(`subCategory[${index}]`, fileObj?.subCategory ?? '');
      if (mode) formData.append(`mutation[${index}]`, mode);
    });
    handleUpload(formData)
  }

  const handleDelete = (index: number) => {
    // Logic for handling delete action
    setSelectedFilesCollection((prevFiles: FileWithMetaType[]) => prevFiles.filter((_: FileWithMetaType, i: number) => i !== index));
  }

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) return;
    const selectedFiles = Array.from(event.target.files || []);
    const { validFiles, errors } = validateFilesWithErrors(selectedFiles);

    if (errors.length > 0) {
      showCustomToast(errors, "error");
    }
    const uniqueNewFiles = validFiles.filter((newFile) =>
      !selectedFilesCollection.some(
        (existingFile) =>
          existingFile?.file?.name === newFile.name &&
          existingFile?.file?.size === newFile.size &&
          existingFile?.file?.lastModified === newFile.lastModified
      )
    );

    if (uniqueNewFiles.length < validFiles.length) {
      toast.warning('File(s) already selected for upload.');
    }
    const filesWithMeta: FileWithMetaType[] = uniqueNewFiles.map(file => ({ file }));
    setSelectedFilesCollection(prev => [...prev, ...filesWithMeta]);
  }

  const handleSelectChange = (event: any, key: keyof FileWithMetaType, index: number) => {
    const { value } = event.target;
    setSelectedFilesCollection((prevFiles: FileWithMetaType[]) => {
      const updatedFiles = [...prevFiles];
      if (updatedFiles[index]) {
        updatedFiles[index][key] = value;
      }
      return updatedFiles;
    });
  }

  const validateSelectedFiles = useMemo(() => {
    let isInvalid = false;
    if (selectedFilesCollection.length === 0) {
      isInvalid = true
    }
    selectedFilesCollection.map((file: FileWithMetaType) => {
      if (!file?.category || !file?.subCategory) {
        isInvalid = true
      }
    })
    return isInvalid;
  }, [selectedFilesCollection])

  const handleSelectPreview = (selectedFile: File) => {
    let file: SelectedFileForPreviewInterface = {
      fileUrl: URL.createObjectURL(selectedFile),
      fileType: selectedFile.type,
      fileName: selectedFile.name
    }
    setSelectedFileForPreview(file)
  }

  const handleCloseFileViewer = () => {
    setSelectedFileForPreview(null)
  }

  const handleConflictDialogClose = () => {
    setShowConflictDialog(false)
  }

  return {
    selectedFilesCollection,
    handleDelete,
    handleSave,
    handleFileSelection,
    handleSelectChange,
    validateSelectedFiles,
    handleAddMoreClick,
    isTableAndAbove,
    addMoreInputRef,
    selectedFileForPreview,
    handleSelectPreview,
    handleCloseFileViewer,
    handleConflictDialogClose,
    showConflictDialog,
    isUploading
  };
}

export default useFMUploadFiles;